import { useCallback, useEffect, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router';
import { Settings as SettingsIcon, ChevronLeft, PieChart } from 'lucide-react';
import { Pill } from '@/ui/Pill';
import { SectionRule } from '@/ui/SectionRule';
import { LangSwitch } from '@/ui/LangSwitch';
import { Modal } from '@/ui/Modal';
import { SettingsScreen } from './Settings';

interface Props {
  /** section-rule label above the cards */
  section: string;
  /** where the Back pill leads; omitted on the home screen */
  backTo?: string;
  children: ReactNode;
}

/**
 * The home frame: title, section rule, cards, toolbar. Home and the per-mode screen share it,
 * only the cards differ. Settings opens as a modal on `#settings`. Esc follows the Back pill.
 */
export function HomeLayout({ section, backTo, children }: Props) {
  const { t } = useTranslation();
  const { pathname, hash } = useLocation();
  const navigate = useNavigate();
  const close = useCallback(() => navigate(pathname, { replace: true }), [navigate, pathname]);

  // Esc steps back like the Back pill, so a few presses from anywhere reach home; an open modal owns Esc instead
  useEffect(() => {
    if (!backTo) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape' && !hash) navigate(backTo);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [backTo, hash, navigate]);

  return (
    <>
      <header className="py-[clamp(16px,4vw,40px)] pb-2 text-center">
        <h1
          className="m-0 font-display text-[clamp(56px,9vw,112px)] leading-none font-black tracking-[-0.02em] italic text-accent"
          style={{ textShadow: '0 0 28px color-mix(in oklch, var(--accent) 45%, transparent)' }}
        >
          <Link to="/" className="text-inherit no-underline hover:text-inherit">
            {t('common.appName')}
          </Link>
        </h1>
        <p className="mt-3.5 text-[12px] font-bold uppercase tracking-[0.28em] text-fg-muted">
          {t('common.tagline')}
        </p>
      </header>

      <nav className="mt-7 flex flex-wrap justify-center gap-3">
        {backTo && (
          <Link to={backTo}>
            <Pill icon={<ChevronLeft />}>{t('common.back')}</Pill>
          </Link>
        )}
        <Link to="/morphology">
          <Pill icon={<PieChart />} active={pathname === '/morphology'}>
            {t('morphology.nav')}
          </Pill>
        </Link>
        <Link to="#settings">
          <Pill icon={<SettingsIcon />}>{t('home.settings')}</Pill>
        </Link>
        <LangSwitch />
      </nav>

      <SectionRule>{section}</SectionRule>

      <div className="stage">{children}</div>

      {hash === '#settings' && (
        <Modal labelledBy="settings-title" onClose={close}>
          <SettingsScreen onClose={close} />
        </Modal>
      )}
    </>
  );
}
